import React, { useEffect, useState } from "react";
import {
  Loader2, FileText, Download, RefreshCw, IndianRupee,
  TrendingUp, AlertCircle, Calendar, Package
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const Stat = ({ icon: Icon, label, value, sub, color = "text-[#1E1B4B]" }) => (
  <div className="bg-white border border-gray-100 rounded-2xl p-4">
    <div className="flex items-center gap-2 text-[#475569] text-[11px] uppercase tracking-wider font-semibold">
      <Icon size={12} /> {label}
    </div>
    <p className={`text-2xl font-extrabold mt-1.5 ${color}`}>{value}</p>
    {sub && <p className="text-[11px] text-[#94a3b8] mt-0.5">{sub}</p>}
  </div>
);

const _firstOfMonth = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
};
const _today = () => new Date().toISOString().slice(0, 10);

/**
 * AccountsTab — single screen for the in-house bookkeeping team.
 *
 * Pick a date range → see month totals, GST split, refunds → download:
 *   • Comprehensive transaction ledger CSV (Tally / Zoho / GSTR-1 ready)
 *   • Refund register CSV
 *   • ZIP of every invoice PDF in the range
 */

const AccountsTab = ({ authAxios }) => {
  const [from, setFrom] = useState(_firstOfMonth());
  const [to, setTo] = useState(_today());
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [s, l] = await Promise.all([
        authAxios.get(`${API_URL}/api/admin/accounts/summary?from_date=${from}&to_date=${to}`),
        authAxios.get(`${API_URL}/api/admin/accounts/list?from_date=${from}&to_date=${to}&limit=200`),
      ]);
      setSummary(s.data);
      setRows(l.data?.rows || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load accounts data");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadHref = (path) =>
    `${API_URL}${path}?from_date=${from}&to_date=${to}`;

  // We want the auth token in the request — for direct downloads we use a hidden
  // form-post-like fetch + blob trick so the X-Admin-Auth header travels too.
  const triggerDownload = async (path, fallbackName) => {
    try {
      const r = await authAxios.get(`${API_URL}${path}?from_date=${from}&to_date=${to}`, { responseType: "blob" });
      const blob = r.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (r.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || fallbackName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      alert(`Download failed: ${detail || "unknown error"}`);
    }
  };

  return (
    <div className="space-y-5" data-testid="accounts-tab">
      {/* Header + date range */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-emerald-700 to-teal-700 text-white flex flex-wrap items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <FileText size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold leading-tight">Accounts &amp; Bookkeeping</h3>
            <p className="text-[11px] text-white/80">For monthly GST filing · Tally / Zoho / GSTR-1 ready exports</p>
          </div>
          <button
            onClick={load}
            className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-[11px] font-bold flex items-center gap-1"
            data-testid="accounts-refresh-btn"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="p-5 grid sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-[11px] font-semibold text-[#475569] uppercase tracking-wider mb-1">From date</label>
            <input
              type="date"
              value={from}
              onChange={e => setFrom(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-emerald-600"
              data-testid="accounts-from-date"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[#475569] uppercase tracking-wider mb-1">To date</label>
            <input
              type="date"
              value={to}
              onChange={e => setTo(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-emerald-600"
              data-testid="accounts-to-date"
            />
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="py-2 rounded-xl bg-[#1E1B4B] text-white text-sm font-bold hover:bg-[#2D2A5F] transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="accounts-apply-range-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Calendar size={14} />}
            Apply range
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3 text-sm text-rose-700 flex items-center gap-2" data-testid="accounts-error">
          <AlertCircle size={14} /> {err}
        </div>
      )}

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="accounts-summary">
          <Stat icon={Package} label="Bookings" value={summary.bookings_count.toLocaleString("en-IN")} sub={`${summary.from} → ${summary.to}`} />
          <Stat icon={IndianRupee} label="Gross revenue" value={inr(summary.gross_revenue_inr)} color="text-emerald-700" />
          <Stat icon={TrendingUp} label="Net (after refunds)" value={inr(summary.net_revenue_inr)} sub={`${summary.refund_count} refunds · ${inr(summary.refund_total_inr)}`} color="text-emerald-700" />
          <Stat icon={FileText} label="Total GST collected" value={inr(summary.tax_breakdown_inr.total_gst)} sub={`CGST ${inr(summary.tax_breakdown_inr.cgst)} · SGST ${inr(summary.tax_breakdown_inr.sgst)} · IGST ${inr(summary.tax_breakdown_inr.igst)}`} color="text-[#DC283C]" />
        </div>
      )}

      {/* Big download buttons */}
      <div className="grid sm:grid-cols-3 gap-3">
        <button
          onClick={() => triggerDownload("/api/admin/accounts/ledger.csv", "ledger.csv")}
          className="bg-white border-2 border-emerald-200 rounded-2xl p-5 text-left hover:border-emerald-500 hover:shadow-md transition group"
          data-testid="accounts-download-ledger"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-700"><Download size={18} /></div>
            <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">CSV</span>
          </div>
          <p className="text-sm font-bold text-[#1E1B4B] mb-1">Transaction ledger</p>
          <p className="text-[11px] text-[#475569] leading-snug">One row per booking. Columns map to GSTR-1 / Tally / Zoho. Excel-friendly UTF-8 with BOM.</p>
        </button>

        <button
          onClick={() => triggerDownload("/api/admin/accounts/invoices.zip", "invoices.zip")}
          className="bg-white border-2 border-[#DC283C]/30 rounded-2xl p-5 text-left hover:border-[#DC283C] hover:shadow-md transition group"
          data-testid="accounts-download-invoices"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#DC283C]/10 flex items-center justify-center text-[#DC283C]"><FileText size={18} /></div>
            <span className="text-[10px] uppercase tracking-wider text-[#DC283C] font-bold">ZIP · {summary?.bookings_count || 0} PDFs</span>
          </div>
          <p className="text-sm font-bold text-[#1E1B4B] mb-1">All invoices (PDF bundle)</p>
          <p className="text-[11px] text-[#475569] leading-snug">Every tax-invoice PDF for the date range, named by invoice number. Ready to email to your CA.</p>
        </button>

        <button
          onClick={() => triggerDownload("/api/admin/accounts/refunds.csv", "refunds.csv")}
          className="bg-white border-2 border-rose-200 rounded-2xl p-5 text-left hover:border-rose-500 hover:shadow-md transition group"
          data-testid="accounts-download-refunds"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center text-rose-600"><RefreshCw size={18} /></div>
            <span className="text-[10px] uppercase tracking-wider text-rose-600 font-bold">CSV · {summary?.refund_count || 0} refunds</span>
          </div>
          <p className="text-sm font-bold text-[#1E1B4B] mb-1">Refund register</p>
          <p className="text-[11px] text-[#475569] leading-snug">Razorpay refund IDs, amounts, reasons. For credit-note + GSTR-1 amendments.</p>
        </button>
      </div>

      {/* Bookings table */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h4 className="text-sm font-bold text-[#1E1B4B]">Bookings &amp; Invoices · {rows.length} shown</h4>
          {loading && <Loader2 size={14} className="animate-spin text-[#475569]" />}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="accounts-table">
            <thead className="bg-[#FAFAFB] border-b border-gray-100">
              <tr className="text-left text-[#475569] uppercase tracking-wider text-[10px]">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Booking ID</th>
                <th className="px-4 py-3">Invoice #</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Package</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">CGST</th>
                <th className="px-4 py-3 text-right">SGST</th>
                <th className="px-4 py-3 text-right">IGST</th>
                <th className="px-4 py-3 text-right">Paid</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-center">PDF</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={12} className="px-4 py-12 text-center text-[#94a3b8] italic">No paid bookings in this date range yet.</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.booking_id} className="border-b border-gray-50 hover:bg-[#FFFAF5]/40">
                  <td className="px-4 py-2.5">{r.booking_date}</td>
                  <td className="px-4 py-2.5 font-mono text-[10px]">{r.booking_id}</td>
                  <td className="px-4 py-2.5 font-mono text-[10px]">{r.invoice_number || <span className="text-[#94a3b8] italic">—</span>}</td>
                  <td className="px-4 py-2.5"><span className="block truncate max-w-[140px]">{r.customer_name}</span><span className="text-[10px] text-[#94a3b8]">{r.customer_phone}</span></td>
                  <td className="px-4 py-2.5">{r.package}</td>
                  <td className="px-4 py-2.5 text-right font-semibold">{inr(r.total_invoice_inr)}</td>
                  <td className="px-4 py-2.5 text-right text-[#475569]">{inr(r.cgst_inr)}</td>
                  <td className="px-4 py-2.5 text-right text-[#475569]">{inr(r.sgst_inr)}</td>
                  <td className="px-4 py-2.5 text-right text-[#475569]">{inr(r.igst_inr)}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-emerald-700">{inr(r.amount_paid_inr)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      r.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" :
                      r.payment_status === "refunded" ? "bg-rose-50 text-rose-700" :
                      r.payment_status === "partial_refund" ? "bg-amber-50 text-amber-700" :
                      "bg-gray-100 text-gray-600"
                    }`}>{r.payment_status}</span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <button
                      onClick={() => triggerDownload(`/api/admin/bookings/invoice.pdf?booking_id=${encodeURIComponent(r.booking_id)}`, `${(r.invoice_number || r.booking_id).replace(/\//g, "_")}.pdf`)}
                      className="text-[#DC283C] hover:text-[#D62F7E] font-semibold"
                      data-testid={`pdf-${r.booking_id}`}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccountsTab;
